import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { supabase } from '../lib/supabase';

export default function UpdatePassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [session, setSession] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [sessionError, setSessionError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const handlePasswordRecovery = async () => {
      try {
        
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        let accessToken = hashParams.get('access_token');
        let refreshToken = hashParams.get('refresh_token');
        let type = hashParams.get('type');

        
        if (!accessToken) {
          const queryParams = new URLSearchParams(window.location.search);
          accessToken = queryParams.get('access_token');
          refreshToken = queryParams.get('refresh_token');
          type = queryParams.get('type');
          
          
          if (!accessToken) {
            const token = queryParams.get('token');
            const tokenType = queryParams.get('type');
            if (token && tokenType === 'recovery') {
              const { data, error } = await supabase.auth.verifyOtp({
                token_hash: token,
                type: 'recovery',
              });
              
              if (!error && data.session && isMounted) {
                setSession(data.session);
                setCheckingSession(false);
                window.history.replaceState(null, '', window.location.pathname);
                return;
              }
            }
          }
        }


        
        if (accessToken && type === 'recovery') {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          if (error) {
            if (isMounted) {
              setSessionError(true);
              setCheckingSession(false);
            }
            return;
          }

          if (data.session && isMounted) {
            setSession(data.session);
            setCheckingSession(false);
            
            window.history.replaceState(null, '', window.location.pathname);
            return;
          }
        }

        
        await new Promise(resolve => setTimeout(resolve, 500));

        
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        
        if (existingSession && isMounted) {
          setSession(existingSession);
          setCheckingSession(false);
          return;
        }

        
        if (isMounted) {
          setSessionError(true);
          setCheckingSession(false);
        }

      } catch (error) {
        console.error('Error in handlePasswordRecovery:', error);
        if (isMounted) {
          setSessionError(true);
          setCheckingSession(false);
        }
      }
    };

    handlePasswordRecovery();

    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        if (session && isMounted) {
          setSession(session);
          setCheckingSession(false);
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Parolele nu coincid!',
      });
      return;
    }

    if (password.length < 6) {
      setMessage({
        type: 'error',
        text: 'Parola trebuie să aibă minim 6 caractere.',
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      setMessage({
        type: 'success',
        text: 'Parola a fost schimbată cu succes! Te redirecționăm...',
      });

      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'A apărut o eroare. Încearcă din nou.',
      });
    } finally {
      setLoading(false);
    }
  };

  
  if (checkingSession) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Se verifică sesiunea...
        </Typography>
      </Container>
    );
  }

  
  if (sessionError || !session) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom color="error">
            Link invalid sau expirat
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Link-ul de resetare a parolei este invalid sau a expirat.
            Te rugăm să soliciți un nou link de resetare.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/reset-password')}
            sx={{ mr: 2 }}
          >
            Solicită link nou
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/login')}
          >
            Înapoi la login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom align="center">
          Setează parola nouă
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Introdu parola nouă pentru contul tău.
        </Typography>

        {message.text && (
          <Alert severity={message.type} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        <form onSubmit={handleUpdatePassword}>
          <TextField
            fullWidth
            label="Parola nouă"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            margin="normal"
            disabled={loading}
          />
          
          <TextField
            fullWidth
            label="Confirmă parola nouă"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            margin="normal"
            disabled={loading}
          />
          
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{ mt: 3, mb: 2 }}
          >
            {loading ? 'Se actualizează...' : 'Setează parola nouă'}
          </Button>
        </form>
        
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            După setarea parolei, vei fi redirecționat către dashboard.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}